import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
    Package, Heart, Award, Settings, LogOut, RefreshCw, Truck, Star, Gift, LayoutDashboard,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";

function OrderCard({ order, onReorder }) {
    return (
        <div className="rounded-2xl border border-caramel/10 bg-white p-5 shadow-warm" data-testid={`order-${order.order_number}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                    <p className="font-serif text-lg text-roast">{order.order_number}</p>
                    <p className="text-xs text-mocha">{new Date(order.created_at).toLocaleDateString()}</p>
                </div>
                <span className="rounded-full bg-banana/25 px-3 py-1 text-xs font-medium capitalize text-caramel">{order.tracking_status}</span>
            </div>
            <div className="mt-4 flex -space-x-3">
                {order.items.slice(0, 5).map((it, i) => (
                    it.image ? <img key={i} src={it.image} alt="" className="h-11 w-11 rounded-full border-2 border-white object-cover" /> :
                    <div key={i} className="grid h-11 w-11 place-items-center rounded-full border-2 border-white bg-cream-200 text-caramel"><Gift size={16} /></div>
                ))}
            </div>
            <div className="mt-4 flex items-center justify-between">
                <span className="font-semibold text-roast">${order.total.toFixed(2)}</span>
                <div className="flex gap-2">
                    <Link to={`/track?order=${order.order_number}`} className="inline-flex items-center gap-1.5 rounded-full border border-caramel/20 px-4 py-2 text-sm font-medium text-caramel hover:bg-cream-200"><Truck size={15} /> Track</Link>
                    <button onClick={() => onReorder(order)} data-testid={`reorder-${order.order_number}`} className="inline-flex items-center gap-1.5 rounded-full bg-roast px-4 py-2 text-sm font-medium text-cream-50 hover:bg-caramel"><RefreshCw size={15} /> Reorder</button>
                </div>
            </div>
        </div>
    );
}

export default function Account() {
    const { user, ready, logout, setUser } = useAuth();
    const { addItem, addBox } = useCart();
    const navigate = useNavigate();

    const { data: orders } = useQuery({ queryKey: ["orders"], queryFn: async () => (await api.get("/orders")).data, enabled: !!user });
    const { data: favorites } = useQuery({ queryKey: ["favorites"], queryFn: async () => (await api.get("/favorites")).data, enabled: !!user });

    React.useEffect(() => { if (ready && user === null) navigate("/login"); }, [ready, user, navigate]);
    if (!user) return <div className="grid min-h-[60vh] place-items-center"><div className="h-10 w-10 animate-spin rounded-full border-2 border-caramel border-t-transparent" /></div>;

    const reorder = (order) => {
        order.items.forEach((it) => {
            if (it.kind === "box") addBox({ box_key: it.box_key, label: it.name, flavors: it.flavors || [], price: it.unit_price, quantity: it.quantity });
            else addItem({ product_id: it.product_id, slug: it.slug, name: it.name, image: it.image, size_key: it.size_key || "single", size_label: "", unit_price: it.unit_price, quantity: it.quantity });
        });
        toast.success("Added to cart — reorder ready!");
    };

    const savePref = async (field, value) => {
        setUser({ ...user, [field]: value });
        toast.success("Preferences updated");
    };

    const doLogout = async () => { await logout(); navigate("/"); };

    return (
        <div className="mx-auto max-w-5xl px-5 py-12 lg:px-8">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="grid h-16 w-16 place-items-center overflow-hidden rounded-full bg-caramel text-2xl font-semibold text-cream-50">
                        {user.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : (user.name?.[0] || "S").toUpperCase()}
                    </div>
                    <div>
                        <h1 className="font-serif text-3xl text-roast">Hi, {user.name?.split(" ")[0] || "there"} 👋</h1>
                        <p className="text-mocha">{user.email}{user.role === "admin" && <span className="ml-2 rounded-full bg-banana px-2 py-0.5 text-xs font-semibold text-roast">Admin</span>}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {user.role === "admin" && (
                        <Link to="/admin" data-testid="admin-dashboard-link" className="inline-flex items-center gap-2 rounded-full bg-caramel px-4 py-2.5 text-sm font-semibold text-cream-50 hover:bg-caramel-dark">
                            <LayoutDashboard size={16} /> Admin Dashboard
                        </Link>
                    )}
                    <Button onClick={doLogout} data-testid="logout-btn" variant="outline" className="rounded-full border-caramel/20 text-caramel hover:bg-cream-200"><LogOut size={16} className="mr-2" /> Sign out</Button>
                </div>
            </div>

            <Tabs defaultValue="orders" className="mt-10">
                <TabsList className="flex w-full flex-wrap justify-start gap-2 bg-transparent p-0">
                    {[["orders", "Orders", Package], ["favorites", "Favorites", Heart], ["rewards", "Rewards", Award], ["prefs", "Preferences", Settings]].map(([v, l, Icon]) => (
                        <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="rounded-full border border-caramel/15 bg-white px-5 py-2.5 data-[state=active]:bg-roast data-[state=active]:text-cream-50">
                            <Icon size={16} className="mr-2" /> {l}
                        </TabsTrigger>
                    ))}
                </TabsList>

                <TabsContent value="orders" className="mt-6">
                    {orders?.length ? (
                        <div className="grid gap-4 sm:grid-cols-2">{orders.map((o) => <OrderCard key={o.order_number} order={o} onReorder={reorder} />)}</div>
                    ) : (
                        <div className="rounded-3xl border border-dashed border-caramel/20 bg-white py-16 text-center">
                            <Package size={40} className="mx-auto text-caramel/50" />
                            <p className="mt-3 font-serif text-xl text-roast">No orders yet</p>
                            <p className="text-mocha">Your delicious history will appear here.</p>
                            <Link to="/shop" className="mt-5 inline-flex rounded-full bg-banana px-6 py-3 font-semibold text-roast hover:bg-banana-hover">Shop Flavors</Link>
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="favorites" className="mt-6">
                    {favorites?.length ? (
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {favorites.map((p) => (
                                <Link key={p.id} to={`/product/${p.slug}`} className="flex items-center gap-4 rounded-2xl border border-caramel/10 bg-white p-4 shadow-warm hover:-translate-y-1 transition-transform">
                                    <img src={p.images?.[0]} alt={p.name} className="h-16 w-16 rounded-xl object-cover" />
                                    <div><p className="font-medium text-roast">{p.name}</p><p className="flex items-center gap-1 text-sm text-gold"><Star size={13} className="fill-gold" /> {p.rating}</p></div>
                                </Link>
                            ))}
                        </div>
                    ) : (
                        <div className="rounded-3xl border border-dashed border-caramel/20 bg-white py-16 text-center">
                            <Heart size={40} className="mx-auto text-caramel/50" />
                            <p className="mt-3 font-serif text-xl text-roast">No favorites saved</p>
                            <p className="text-mocha">Tap the heart on any flavor to save it here.</p>
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="rewards" className="mt-6">
                    <div className="rounded-3xl bg-gradient-to-br from-banana to-banana-deep p-8 text-roast shadow-warm">
                        <p className="text-sm font-medium">Sweet Pearls Rewards</p>
                        <p className="mt-2 font-serif text-5xl">{user.rewards_points || 0} <span className="text-2xl">pts</span></p>
                        <p className="mt-2 text-roast/70">Earn 1 point per $1 spent. 100 points = a free cup 🍨</p>
                        <div className="mt-4 h-2 overflow-hidden rounded-full bg-roast/15"><div className="h-full rounded-full bg-roast" style={{ width: `${Math.min(100, (user.rewards_points || 0))}%` }} /></div>
                    </div>
                </TabsContent>

                <TabsContent value="prefs" className="mt-6 space-y-3">
                    <label className="flex items-center justify-between rounded-2xl border border-caramel/10 bg-white p-5">
                        <div><p className="font-medium text-roast">Email updates</p><p className="text-sm text-mocha">New flavors, offers & drops.</p></div>
                        <Switch checked={user.email_opt_in} onCheckedChange={(v) => savePref("email_opt_in", v)} data-testid="pref-email" />
                    </label>
                    <label className="flex items-center justify-between rounded-2xl border border-caramel/10 bg-white p-5">
                        <div><p className="font-medium text-roast">SMS updates</p><p className="text-sm text-mocha">Order status & local pop-ups.</p></div>
                        <Switch checked={user.sms_opt_in} onCheckedChange={(v) => savePref("sms_opt_in", v)} data-testid="pref-sms" />
                    </label>
                    <div className="rounded-2xl border border-caramel/10 bg-white p-5">
                        <p className="font-medium text-roast">Saved addresses</p>
                        <p className="text-sm text-mocha">You have no saved addresses yet. Addresses are collected securely at checkout.</p>
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}
